import { useState, useRef, useEffect } from 'react';
import { COUNTRIES, COUNTRY_MAP } from '../lib/countries';

interface TargetingData {
  countries: string | null;
  platforms: string | null;
  device_types: string | null;
  languages: string | null;
}

interface TargetingFormProps {
  value: TargetingData;
  onChange: (targeting: TargetingData) => void;
  offerGeos?: string[];
}

const PLATFORM_OPTIONS = [
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

const DEVICE_TYPE_OPTIONS = [
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
];

// Common languages for ad targeting
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'zh', name: 'Chinese' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'bn', name: 'Bengali' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'de', name: 'German' },
  { code: 'ko', name: 'Korean' },
  { code: 'fr', name: 'French' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'te', name: 'Telugu' },
  { code: 'tr', name: 'Turkish' },
  { code: 'ta', name: 'Tamil' },
  { code: 'it', name: 'Italian' },
  { code: 'th', name: 'Thai' },
  { code: 'pl', name: 'Polish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ms', name: 'Malay' },
  { code: 'cs', name: 'Czech' },
  { code: 'ro', name: 'Romanian' },
  { code: 'el', name: 'Greek' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'he', name: 'Hebrew' },
  { code: 'da', name: 'Danish' },
  { code: 'fi', name: 'Finnish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'hr', name: 'Croatian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'et', name: 'Estonian' },
];

const LANGUAGE_MAP = LANGUAGES.reduce<Record<string, typeof LANGUAGES[0]>>((acc, l) => {
  acc[l.code] = l;
  return acc;
}, {});


export function TargetingForm({ value, onChange, offerGeos }: TargetingFormProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Advanced GEO/platform/device targeting starts collapsed for new ads, but
  // auto-expands when an existing ad already carries any of these filters.
  const [showAdvanced, setShowAdvanced] = useState(
    () => !!(value.countries || value.platforms || value.device_types)
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Parse comma-separated strings into arrays
  const parseList = (str: string | null): string[] => {
    if (!str) return [];
    return str.split(',').map((s) => s.trim()).filter(Boolean);
  };

  // Convert array back to comma-separated string
  const toListString = (arr: string[]): string | null => {
    if (arr.length === 0) return null;
    return arr.join(',');
  };

  const selectedCountries = parseList(value.countries);
  const selectedPlatforms = parseList(value.platforms);
  const selectedDeviceTypes = parseList(value.device_types);
  const selectedLanguages = parseList(value.languages);

  // Filter countries based on search term
  const filteredCountries = COUNTRIES.filter((country) => {
    const search = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(search) ||
      country.code.toLowerCase().includes(search)
    );
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryToggle = (code: string) => {
    const current = parseList(value.countries);
    const updated = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];
    onChange({ ...value, countries: toListString(updated) });
  };

  const handleRemoveCountry = (code: string) => {
    const current = parseList(value.countries);
    const updated = current.filter((c) => c !== code);
    onChange({ ...value, countries: toListString(updated) });
  };

  const handlePlatformToggle = (platform: string) => {
    const current = parseList(value.platforms);
    const updated = current.includes(platform)
      ? current.filter((p) => p !== platform)
      : [...current, platform];
    onChange({ ...value, platforms: toListString(updated) });
  };

  const handleDeviceTypeToggle = (deviceType: string) => {
    const current = parseList(value.device_types);
    const updated = current.includes(deviceType)
      ? current.filter((d) => d !== deviceType)
      : [...current, deviceType];
    onChange({ ...value, device_types: toListString(updated) });
  };

  const handleLanguageToggle = (language: string) => {
    const current = parseList(value.languages);
    const updated = current.includes(language)
      ? current.filter((l) => l !== language)
      : [...current, language];
    onChange({ ...value, languages: toListString(updated) });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-content-muted mb-2">Targeting</div>

      {/* Languages — primary axis. One creative per language; the tracker link
          (click_url) splits traffic by GEO downstream. */}
      <div>
        <label className="block text-sm font-medium text-content-muted mb-1">
          Languages
        </label>
        <p className="mb-2 text-xs text-content-subtle">
          One creative per language — the tracker link splits by GEO. Leave unchecked to target all languages.
        </p>
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map((lang) => {
            const isSelected = selectedLanguages.includes(lang.code);
            return (
              <label
                key={lang.code}
                className={`
                  inline-flex items-center px-2.5 py-1.5 rounded-md border cursor-pointer
                  transition-colors text-sm
                  ${
                    isSelected
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border-strong hover:border-border-strong'
                  }
                `}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleLanguageToggle(lang.code)}
                  className="sr-only"
                />
                <span>{lang.name}</span>
                <span className="ml-1 text-xs text-content-subtle">({lang.code})</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Advanced targeting toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="flex items-center gap-1.5 text-sm font-medium text-content-muted hover:text-content"
      >
        <svg
          className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Advanced targeting (optional)
      </button>

      {showAdvanced && (
        <div className="space-y-4">
          {/* Countries - Multi-select with search */}
          <div ref={dropdownRef} className="relative">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-content-muted">
                Countries
              </label>
              {offerGeos && offerGeos.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    onChange({ ...value, countries: offerGeos.join(',') });
                  }}
                  className="text-xs text-primary hover:text-primary font-medium"
                >
                  Offer GEOs ({offerGeos.length})
                </button>
              )}
            </div>

            {/* Selected countries tags */}
            {selectedCountries.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedCountries.map((code) => {
                  const country = COUNTRY_MAP[code];
                  return (
                    <span
                      key={code}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-sm"
                    >
                      <span>{country?.flag || '🌍'}</span>
                      <span>{country?.name || code}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCountry(code)}
                        className="ml-1 hover:text-primary/70"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Search input */}
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder={selectedCountries.length > 0 ? "Add more countries..." : "Search countries..."}
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                <svg className="w-4 h-4 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Dropdown list */}
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full max-w-md bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredCountries.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-content-subtle">
                    No countries found
                  </div>
                ) : (
                  filteredCountries.map((country) => {
                    const isSelected = selectedCountries.includes(country.code);
                    return (
                      <button
                        key={country.code}
                        type="button"
                        onClick={() => handleCountryToggle(country.code)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-muted ${
                          isSelected ? 'bg-primary/5' : ''
                        }`}
                      >
                        <span className="text-base">{country.flag}</span>
                        <span className="flex-1">{country.name}</span>
                        <span className="text-xs text-content-subtle">{country.code}</span>
                        {isSelected && (
                          <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}

            <p className="mt-1 text-xs text-content-subtle">
              Leave empty to target all countries
            </p>
          </div>

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-2">
              Platforms
            </label>
            <div className="flex flex-wrap gap-3">
              {PLATFORM_OPTIONS.map((option) => {
                const isSelected = selectedPlatforms.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`
                      inline-flex items-center px-3 py-2 rounded-md border cursor-pointer
                      transition-colors
                      ${
                        isSelected
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border-strong hover:border-border-strong'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handlePlatformToggle(option.value)}
                      className="sr-only"
                    />
                    <span className="flex items-center gap-2">
                      {option.value === 'ios' ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.523 15.341c-.5 0-.91-.41-.91-.91V9.57c0-.5.41-.91.91-.91s.91.41.91.91v4.861c0 .5-.41.91-.91.91zm-11.046 0c-.5 0-.91-.41-.91-.91V9.57c0-.5.41-.91.91-.91s.91.41.91.91v4.861c0 .5-.41.91-.91.91zm11.523-9.79l1.228-2.235a.255.255 0 0 0-.448-.247l-1.245 2.266a7.634 7.634 0 0 0-3.535-.856c-1.282 0-2.476.308-3.535.856L9.22 3.07a.255.255 0 0 0-.448.247L10 5.552a7.227 7.227 0 0 0-3.5 6.146v.312h11v-.312a7.227 7.227 0 0 0-3.5-6.146zM9 9.23a.77.77 0 1 1 .002-1.54A.77.77 0 0 1 9 9.23zm6 0a.77.77 0 1 1 .002-1.54A.77.77 0 0 1 15 9.23zM6.5 12.01h11v5.5c0 .827-.673 1.5-1.5 1.5H15v2.08c0 .5-.41.91-.91.91s-.91-.41-.91-.91v-2.08h-2.36v2.08c0 .5-.41.91-.91.91s-.91-.41-.91-.91v-2.08H8c-.827 0-1.5-.673-1.5-1.5v-5.5z"/>
                        </svg>
                      )}
                      <span className="text-sm">{option.label}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-content-subtle">
              Leave unchecked to target all platforms
            </p>
          </div>

          {/* Device Types */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-2">
              Device Types
            </label>
            <div className="flex flex-wrap gap-3">
              {DEVICE_TYPE_OPTIONS.map((option) => {
                const isSelected = selectedDeviceTypes.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`
                      inline-flex items-center px-3 py-2 rounded-md border cursor-pointer
                      transition-colors
                      ${
                        isSelected
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border-strong hover:border-border-strong'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleDeviceTypeToggle(option.value)}
                      className="sr-only"
                    />
                    <span className="flex items-center gap-2">
                      {option.value === 'phone' ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                      <span className="text-sm">{option.label}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-content-subtle">
              Leave unchecked to target all device types
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Helper to format targeting data for display
 */
export function formatTargeting(targeting?: {
  countries?: string | null;
  platforms?: string | null;
  device_types?: string | null;
  languages?: string | null;
} | null): string {
  if (!targeting) return 'All';

  const parts: string[] = [];

  if (targeting.countries) {
    const codes = targeting.countries.split(',').slice(0, 3);
    const names = codes.map((code) => COUNTRY_MAP[code.trim()]?.name || code.trim());
    parts.push(names.join(', '));
    if (targeting.countries.split(',').length > 3) {
      parts[parts.length - 1] += '...';
    }
  }

  if (targeting.platforms) {
    const platforms = targeting.platforms.split(',');
    if (platforms.length < 2) {
      parts.push(platforms[0] === 'ios' ? 'iOS' : 'Android');
    }
  }

  if (targeting.device_types) {
    const types = targeting.device_types.split(',');
    if (types.length < 2) {
      parts.push(types[0].charAt(0).toUpperCase() + types[0].slice(1));
    }
  }

  if (targeting.languages) {
    const codes = targeting.languages.split(',').slice(0, 3);
    const names = codes.map((code) => LANGUAGE_MAP[code.trim()]?.name || code.trim());
    parts.push(names.join(', '));
    if (targeting.languages.split(',').length > 3) {
      parts[parts.length - 1] += '...';
    }
  }

  return parts.length > 0 ? parts.join(' | ') : 'All';
}
